import { useEffect, useState } from 'react'
import Swal from 'sweetalert2'
import IPButton from '../../../../components/IPButton'
import IPInput from '../../../../components/IPInput'
import IPModal from '../../../../components/IPModal'
import ExcelButton from '../../../../components/IPExcelButton/IPExcelButton'
import InputCampaign from '../../../../components/InputCampaign'
import { BaseComponentWithContextProps } from '../../../../base/customHooks/componentHOC'
import { IException } from '../../../../base/models'
import { exportToXLSX } from '../../../../base/utils/dataHelper'
import { DateInputValue, toApiDate, toDateInputValue, validateDateRange } from '../models/dateInput'
import {
  appleDepStatusClass,
  AppleDepOrderTypeLabels,
  AppleDepStatusLabels,
  IAppleDepTransaction,
} from '../models/models'
import {
  CheckAppleDepTransactionStatusApi,
  GetAppleDepTransactionDetailApi,
  GetAppleDepTransactionsApi,
  RetryAppleDepFailedDevicesApi,
} from '../services/appleDepApi'

const startOfMonth = () => {
  const date = new Date()
  date.setDate(1)
  date.setHours(0, 0, 0, 0)
  return date
}


const formatDisplayDateTime = (value?: string) => {
  if (!value) return '-'
  const date = new Date(value)
  return isNaN(date.getTime()) ? value : date.toLocaleString('tr-TR')
}

/**
 * Gönderimlerim sekmesi. Apple'a yapılan her Bulk Enroll gönderimi ve asenkron
 * sonucu burada izlenir. Statü saatlik job ile güncellenir; "Statü Sorgula"
 * butonu aynı sorguyu anında yapar.
 */
export default function TransactionsTab(props: BaseComponentWithContextProps) {
  const [startDate, setStartDate] = useState<DateInputValue>(startOfMonth())
  const [endDate, setEndDate] = useState<DateInputValue>(new Date())
  const [searchText, setSearchText] = useState('')
  const [onlyEscalated, setOnlyEscalated] = useState(false)

  const [transactions, setTransactions] = useState<IAppleDepTransaction[]>([])
  const [detail, setDetail] = useState<IAppleDepTransaction | undefined>(undefined)
  const [showDetail, setShowDetail] = useState(false)

  const showError = (err: any, fallback = 'İşlem sırasında bir hata oluştu.') => {
    const exception = err as IException
    Swal.fire({
      icon: 'error',
      title: exception?.description ?? fallback,
      showConfirmButton: true,
      allowOutsideClick: false,
    })
  }

  const loadTransactions = async () => {
    const dateError = validateDateRange(startDate, endDate)
    if (dateError) {
      Swal.fire({ icon: 'warning', title: dateError })
      return
    }

    props.showLoading()
    try {
      const api: GetAppleDepTransactionsApi = new GetAppleDepTransactionsApi({})
      const result = await api.getTransactions({
        StartDate: toApiDate(startDate),
        EndDate: toApiDate(endDate),
        TransactionId: searchText,
        OnlyEscalated: onlyEscalated,
      })
      setTransactions(result ?? [])
    } catch (err: any) {
      showError(err, 'Gönderim listesi alınamadı.')
    } finally {
      props.hideLoading()
    }
  }

  useEffect(() => {
    loadTransactions()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const openDetail = async (transaction: IAppleDepTransaction) => {
    props.showLoading()
    try {
      const api: GetAppleDepTransactionDetailApi = new GetAppleDepTransactionDetailApi({})
      setDetail(await api.getTransactionDetail(transaction.Id))
      setShowDetail(true)
    } catch (err: any) {
      showError(err, 'Gönderim detayı alınamadı.')
    } finally {
      props.hideLoading()
    }
  }

  const checkStatus = async (transaction: IAppleDepTransaction) => {
    props.showLoading()
    try {
      const api: CheckAppleDepTransactionStatusApi = new CheckAppleDepTransactionStatusApi({})
      const updated = await api.checkStatus(transaction.Id)
      await loadTransactions()

      if (showDetail) setDetail(updated)

      Swal.fire({
        icon: 'info',
        title: 'Statü güncellendi',
        text: `${AppleDepStatusLabels[updated?.Status] ?? updated?.Status ?? ''}`,
        timer: 2000,
        showConfirmButton: false,
      })
    } catch (err: any) {
      showError(err, 'Statü sorgulanamadı.')
    } finally {
      props.hideLoading()
    }
  }

  const retryFailed = async (transaction: IAppleDepTransaction) => {
    const confirmation = await Swal.fire({
      icon: 'question',
      title: 'Hatalı cihazlar yeniden gönderilsin mi?',
      text: "Apple, hatalı kayıtların başarılı olanlardan ayrı gönderilmesini istiyor. Yeni bir gönderim oluşturulacak.",
      showCancelButton: true,
      confirmButtonText: 'Yeniden Gönder',
      cancelButtonText: 'Vazgeç',
    })

    if (!confirmation.isConfirmed) return

    props.showLoading()
    try {
      const api: RetryAppleDepFailedDevicesApi = new RetryAppleDepFailedDevicesApi({})
      const result = await api.retryFailed(transaction.Id)
      await loadTransactions()
      setShowDetail(false)

      Swal.fire({
        icon: 'success',
        title: 'Yeniden gönderim yapıldı',
        text: `İşlem No: ${result?.TransactionId ?? '-'} — ${result?.SentDeviceCount ?? 0} cihaz`,
      })
    } catch (err: any) {
      showError(err, 'Yeniden gönderim başarısız oldu.')
    } finally {
      props.hideLoading()
    }
  }

  const exportToExcel = () => {
    if (transactions.length === 0) {
      Swal.fire({ icon: 'warning', title: 'Aktarılacak kayıt yok.' })
      return
    }

    const rows = transactions.map(transaction => ({
      'İşlem No': transaction.TransactionId,
      'Apple İşlem No': transaction.DeviceEnrollmentTransactionId ?? '',
      'Reseller ID': transaction.DepResellerId,
      'İşlem Tipi': AppleDepOrderTypeLabels[transaction.OrderType] ?? transaction.OrderType,
      'Cihaz Sayısı': transaction.DeviceCount,
      'Hatalı Cihaz': transaction.FailedDeviceCount,
      Durum: AppleDepStatusLabels[transaction.Status] ?? transaction.Status,
      'Apple Statüsü': transaction.StatusCode ?? '',
      'Hata Kodu': transaction.ErrorCode ?? '',
      'Hata Mesajı': transaction.ErrorMessage ?? '',
      Ortam: transaction.IsFake ? 'Simülasyon' : transaction.IsLive ? 'Canlı' : 'Test',
      'Gönderim Tarihi': formatDisplayDateTime(transaction.SentAt),
      'Tamamlanma Tarihi': formatDisplayDateTime(transaction.CompletedOn),
      'Son Statü Sorgusu': formatDisplayDateTime(transaction.LastCheckedAt),
      'Sorgu Sayısı': transaction.CheckCount,
      Eskalasyon: transaction.EscalatedOn ? formatDisplayDateTime(transaction.EscalatedOn) : '',
    }))

    exportToXLSX(rows, 'Apple DEP Gonderimlerim')
  }

  return (
    <div className='appleDepTransactions'>
      <div className='appleDepFilters'>
        <div className='row'>
          <div className='col-12 col-md-2 mb-2'>
            <InputCampaign
              type='date'
              id='AppleDepTxStartDate'
              name='AppleDepTxStartDate'
              label='Başlangıç'
              value={toDateInputValue(startDate)}
              onChange={e => setStartDate(e.target.value)}
              isActive
            />
          </div>
          <div className='col-12 col-md-2 mb-2'>
            <InputCampaign
              type='date'
              id='AppleDepTxEndDate'
              name='AppleDepTxEndDate'
              label='Bitiş'
              value={toDateInputValue(endDate)}
              onChange={e => setEndDate(e.target.value)}
              isActive
            />
          </div>
          <div className='col-12 col-md-3 mb-2'>
            <label className='labeltext'>İşlem No</label>
            <IPInput type='text' value={searchText} onChange={e => setSearchText(e.target.value)} />
          </div>
          <div className='col-12 col-md-3 mb-2 appleDepFilterCheck'>
            <label className='labeltext'>
              <input type='checkbox' checked={onlyEscalated} onChange={e => setOnlyEscalated(e.target.checked)} />{' '}
              Yalnızca 24 saati aşanlar
            </label>
          </div>
          <div className='col-12 col-md-3 mb-2 appleDepFilterAction'>
            <IPButton text='Listele' className='appleDepPrimaryButton' onClick={loadTransactions} />
            <ExcelButton onClick={exportToExcel} />
          </div>
        </div>
      </div>

      {transactions.length === 0 ? (
        <div className='appleDepEmpty'>Seçilen tarih aralığında gönderim bulunamadı.</div>
      ) : (
        <div className='appleDepTableWrapper'>
          <table className='table table-hover appleDepTable'>
            <thead>
              <tr>
                <th>İşlem No</th>
                <th>Apple İşlem No</th>
                <th>Tip</th>
                <th>Cihaz</th>
                <th>Hatalı</th>
                <th>Durum</th>
                <th>Ortam</th>
                <th>Gönderim</th>
                <th>Son Sorgu</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {transactions.map(transaction => (
                <tr key={transaction.Id} className={transaction.EscalatedOn ? 'appleDepRowEscalated' : ''}>
                  <td>
                    <strong>{transaction.TransactionId}</strong>
                  </td>
                  <td className='appleDepMonoCell'>{transaction.DeviceEnrollmentTransactionId ?? '-'}</td>
                  <td>{AppleDepOrderTypeLabels[transaction.OrderType] ?? transaction.OrderType}</td>
                  <td>{transaction.DeviceCount}</td>
                  <td>{transaction.FailedDeviceCount > 0 ? transaction.FailedDeviceCount : '-'}</td>
                  <td>
                    <span className={appleDepStatusClass(transaction.Status)}>
                      {AppleDepStatusLabels[transaction.Status] ?? transaction.Status}
                    </span>
                    {transaction.ErrorCode && (
                      <div className='appleDepBlockReason'>
                        {transaction.ErrorCode}: {transaction.ErrorMessage}
                      </div>
                    )}
                    {transaction.EscalatedOn && (
                      <div className='appleDepBlockReason'>
                        24 saat aşıldı — Apple eskalasyonu gerekiyor
                      </div>
                    )}
                  </td>
                  <td>
                    {transaction.IsFake ? 'Simülasyon' : transaction.IsLive ? 'Canlı' : 'Test'}
                  </td>
                  <td>{formatDisplayDateTime(transaction.SentAt)}</td>
                  <td>{formatDisplayDateTime(transaction.LastCheckedAt)}</td>
                  <td className='appleDepRowActions'>
                    <button type='button' className='appleDepLinkButton' onClick={() => openDetail(transaction)}>
                      Detay
                    </button>
                    <button type='button' className='appleDepLinkButton' onClick={() => checkStatus(transaction)}>
                      Statü Sorgula
                    </button>
                    {transaction.FailedDeviceCount > 0 && (
                      <button
                        type='button'
                        className='appleDepLinkButtonDanger'
                        onClick={() => retryFailed(transaction)}
                      >
                        Hatalıları Gönder
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <IPModal
        show={showDetail}
        onClose={() => setShowDetail(false)}
        hideConfirm
        width={1100}
        title={`Gönderim Detayı — ${detail?.TransactionId ?? ''}`}
      >
        <div className='appleDepDetail'>
          {detail && (
            <>
              <div className='appleDepDetailGrid'>
                <div>
                  <span className='appleDepDetailLabel'>Apple İşlem No</span>
                  <span className='appleDepMonoCell'>{detail.DeviceEnrollmentTransactionId ?? '-'}</span>
                </div>
                <div>
                  <span className='appleDepDetailLabel'>Reseller ID</span>
                  <span>{detail.DepResellerId}</span>
                </div>
                <div>
                  <span className='appleDepDetailLabel'>İşlem Tipi</span>
                  <span>{AppleDepOrderTypeLabels[detail.OrderType] ?? detail.OrderType}</span>
                </div>
                <div>
                  <span className='appleDepDetailLabel'>Durum</span>
                  <span className={appleDepStatusClass(detail.Status)}>
                    {AppleDepStatusLabels[detail.Status] ?? detail.Status}
                  </span>
                </div>
                <div>
                  <span className='appleDepDetailLabel'>Gönderim</span>
                  <span>{formatDisplayDateTime(detail.SentAt)}</span>
                </div>
                <div>
                  <span className='appleDepDetailLabel'>Tamamlanma</span>
                  <span>{formatDisplayDateTime(detail.CompletedOn)}</span>
                </div>
                <div>
                  <span className='appleDepDetailLabel'>Statü Sorgu Sayısı</span>
                  <span>{detail.CheckCount}</span>
                </div>
                <div>
                  <span className='appleDepDetailLabel'>Ortam</span>
                  <span>{detail.IsFake ? 'Simülasyon' : detail.IsLive ? 'Canlı' : 'Test'}</span>
                </div>
              </div>

              {detail.ErrorCode && (
                <div className='appleDepWarning'>
                  <strong>{detail.ErrorCode}</strong> — {detail.ErrorMessage}
                </div>
              )}

              <div className='appleDepTableWrapper'>
                <table className='table table-hover appleDepTable'>
                  <thead>
                    <tr>
                      <th>Seri No / IMEI</th>
                      <th>Sipariş No</th>
                      <th>Sipariş Tarihi</th>
                      <th>Sevkiyat No</th>
                      <th>Organization ID</th>
                      <th>Apple Sonucu</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.Devices?.map(device => (
                      <tr key={device.Id} className={device.IsFailed ? 'appleDepRowFailed' : ''}>
                        <td>
                          <strong>{device.SerialNumber}</strong>
                        </td>
                        <td>{device.OrderNumber}</td>
                        <td>{formatDisplayDateTime(device.OrderDate)}</td>
                        <td>{device.DeliveryNumber ?? '-'}</td>
                        <td>{device.OrganizationId}</td>
                        <td>
                          {device.DevicePostStatus ? (
                            <span
                              className={
                                device.IsFailed
                                  ? 'appleDepBadge appleDepBadgeDanger'
                                  : 'appleDepBadge appleDepBadgeSuccess'
                              }
                            >
                              {device.DevicePostStatus}
                            </span>
                          ) : (
                            <span className='appleDepBadge appleDepBadgeInfo'>Bekliyor</span>
                          )}
                          {device.DevicePostStatusMessage && (
                            <div className='appleDepBlockReason'>{device.DevicePostStatusMessage}</div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className='appleDepDetailButtons'>
                <IPButton
                  text='Statü Sorgula'
                  className='appleDepSecondaryButton'
                  onClick={() => checkStatus(detail)}
                />
                {detail.FailedDeviceCount > 0 && (
                  <IPButton
                    text='Hatalıları Yeniden Gönder'
                    className='appleDepPrimaryButton'
                    onClick={() => retryFailed(detail)}
                  />
                )}
              </div>
            </>
          )}
        </div>
      </IPModal>
    </div>
  )
}
